import { createFileRoute, useLocation, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { PortableTextBlock } from "sanity";
import { codeToHtml } from "shiki";

import { createHead } from "@/components/createHead";
import {
  DocsPortableText,
  DocsTableOfContents,
  Heading,
  MobileTableOfContents,
} from "@/components/DocsPortableText";
import MarketingFooter from "@/components/MarketingFooter";
import { MarketingNavBar } from "@/components/MarketingNavBar";
import { getHeadingId } from "@/components/portableTextComponents";
import { SidebarHelp } from "@/components/SidebarHelp";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { DocData, getDocs } from "@/lib/content";
import { cn } from "@/lib/utils";

function DocsSidebar({
  docs,
  className,
}: {
  docs: DocData[];
  className?: string;
}) {
  const { pathname } = useLocation();
  const docsPages = useMemo(
    () => docs.filter((doc) => doc.tags.includes("docs")),
    [docs],
  );
  const guidesPages = docs.filter((doc) => doc.tags.includes("guide"));
  const referencePages = docs.filter((doc) => doc.tags.includes("reference"));
  const helpPages = docs.filter((doc) => doc.tags.includes("help"));

  return (
    <Sidebar className={className}>
      <SidebarContent>
        <SidebarGroup collapsible="docs">
          <SidebarGroupLabel>Docs</SidebarGroupLabel>
          <SidebarMenu>
            {docsPages.map((doc) => (
              <SidebarMenuItem key={doc.slug.current}>
                <SidebarMenuButton
                  asChild
                  isActive={`/docs/${doc.slug.current}` === pathname}
                >
                  <Link to="/docs/$slug" params={{ slug: doc.slug.current }}>
                    {doc.title}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>
        <SidebarGroup collapsible="guides">
          <SidebarGroupLabel>Guides</SidebarGroupLabel>
          <SidebarMenu>
            {guidesPages.map((doc) => (
              <SidebarMenuItem key={doc.slug.current}>
                <SidebarMenuButton
                  asChild
                  isActive={`/docs/${doc.slug.current}` === pathname}
                >
                  <Link to="/docs/$slug" params={{ slug: doc.slug.current }}>
                    {doc.title}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>
        <SidebarGroup collapsible="reference">
          <SidebarGroupLabel>API</SidebarGroupLabel>
          <SidebarMenu>
            {referencePages.map((doc) => (
              <SidebarMenuItem key={doc.slug.current}>
                <SidebarMenuButton
                  asChild
                  isActive={`/docs/${doc.slug.current}` === pathname}
                >
                  <Link to="/docs/$slug" params={{ slug: doc.slug.current }}>
                    {doc.title}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>
        <SidebarHelp collapsible="help">
          {helpPages.map((doc) => (
            <SidebarMenuItem key={doc.slug.current}>
              <SidebarMenuButton
                asChild
                isActive={`/docs/${doc.slug.current}` === pathname}
              >
                <Link to="/docs/$slug" params={{ slug: doc.slug.current }}>
                  {doc.title}
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarHelp>
      </SidebarContent>
    </Sidebar>
  );
}

async function getHeadings(doc: DocData) {
  doc.body = await Promise.all(
    (doc.body as PortableTextBlock[]).map(async (block) => {
      if (
        block._type === "code" &&
        "code" in block &&
        "language" in block &&
        typeof block.language === "string"
      ) {
        block.code = await codeToHtml(block.code as string, {
          lang: block.language,
          themes: {
            light: "github-light",
            dark: "github-dark",
          },
        });
      }

      return block;
    }),
  );

  return (doc.body as PortableTextBlock[])
    .filter(
      (block) => typeof block.style === "string" && block.style?.match(/^h\d$/),
    )
    .map((block) => {
      return {
        text: block,
        level:
          typeof block.style === "string"
            ? parseInt(block.style?.match(/^h(\d)$/)?.[1] || "0", 10)
            : 0,
        id: getHeadingId(block),
      };
    });
}

export const Route = createFileRoute("/docs/$slug")({
  component: RouteComponent,
  loader: async ({ params }) => {
    const docs = await getDocs();
    const doc = docs.find((post) => post.slug.current === params.slug);

    if (!doc) {
      throw new Error("Doc not found");
    }

    const headings = await getHeadings(doc);

    return {
      docs,
      headings: headings as Omit<
        Awaited<ReturnType<typeof getHeadings>>[number],
        "text"
      >[],
      doc,
    };
  },
  head: ({ loaderData }) =>
    createHead({
      title: `Docs | ${loaderData!.doc.title}`,
      ogTitle: `Docs | ${loaderData!.doc.title}`,
      description: loaderData!.doc.description,
      pathname: `/docs/${loaderData!.doc.slug.current}`,
      ogImage: `/docs/$slug/og.png`,
      ogParams: { slug: loaderData!.doc.slug.current },
    }),
});

function RouteComponent() {
  const { docs, headings, doc } = Route.useLoaderData();

  return (
    <>
      <header className="w-screen md:max-w-[calc(1216px+280px+280px)] mx-auto px-4 md:px-10 xl:px-16 py-4 flex flex-col gap-6 md:gap-8">
        <MarketingNavBar variant="teal" showSearch={true} />
      </header>
      <div className="flex-1">
        <SidebarProvider>
          <div className="flex-1 min-h-0 grid md:max-w-[calc(1216px+280px)] md:grid-cols-[280px_1fr] xl:max-w-[calc(1216px+280px+280px)] xl:grid-cols-[280px_1fr_minmax(0,280px)] mx-auto w-screen md:px-10 xl:px-16">
            <DocsSidebar docs={docs} />
            <div className="xl:contents">
              <div className="w-full bg-slate-subtle px-4 md:px-12 py-3 sticky top-0 border-b border-slate-dim md:hidden flex items-center gap-2">
                <SidebarTrigger static={true} />
                {headings.length > 0 && (
                  <MobileTableOfContents headings={headings as Heading[]} />
                )}
              </div>
              <main
                className={cn(
                  "px-4 md:px-12 pb-20 mx-auto flex flex-col gap-4 md:gap-6 min-w-0 w-full",
                  "pt-8 md:pt-14",
                )}
              >
                <header className="flex flex-col gap-6">
                  <h1 className="scroll-m-20 text-5xl md:text-7xl font-extrabold tracking-tight instrument-serif">
                    {doc.title}
                  </h1>
                  <div className="flex items-center justify-between gap-4 text-skate-dim font-light italic">
                    {doc.description}
                  </div>
                </header>

                <div>
                  <DocsPortableText
                    body={doc.body as PortableTextBlock[]}
                    isNew
                  />
                </div>
              </main>

              <aside
                className="hidden xl:block sticky top-0 py-8 h-fit overflow-auto shrink-0 w-[280px] max-h-screen"
                style={{ scrollbarWidth: "none" }}
              >
                <DocsTableOfContents headings={headings as Heading[]} />
              </aside>
            </div>
          </div>
        </SidebarProvider>

        <MarketingFooter showStartSection={false} />
      </div>
    </>
  );
}
